<script setup lang="ts">
import type { VListCardTag } from "@verbleif/lib/src/components/ListCard/VListCard.types";
import VHelpTooltip from "@verbleif/lib/src/components/HelpTooltip/VHelpTooltip.vue";
import VListCard from "@verbleif/lib/src/components/ListCard/VListCard.vue";

/**
 * One card of the debug tools: a VListCard with a title, an optional question mark with help, actions or a label in
 * the header, rows in the body and actions in the footer.
 */
const {
  title,
  help = undefined,
  count = null,
  as = "dl",
  maxHeight = undefined,
  empty = false,
  emptyText = undefined,
  loading = false,
} = defineProps<{
  title: string
  help?: string
  count?: string | number | null
  /** `dl` for label and value rows (the default), `div` for free content. */
  as?: VListCardTag
  maxHeight?: number | string
  empty?: boolean
  emptyText?: string
  loading?: boolean
}>();

defineSlots<{
  "default"?: () => unknown
  /** Actions or a label on the right of the header. */
  "header-actions"?: () => unknown
  /** Buttons under the rows. */
  "actions"?: () => unknown
}>();
</script>

<template>
  <VListCard
    :as="as"
    :count="count"
    :max-height="maxHeight"
    :empty="empty"
    :empty-text="emptyText"
    :loading="loading"
    :aria-label="title"
  >
    <template #title="{ id }">
      <span :id="id" class="truncate font-medium">{{ title }}</span>
      <VHelpTooltip v-if="help" :content="help" :subject="title" />
    </template>
    <template v-if="$slots['header-actions']" #header-actions>
      <slot name="header-actions" />
    </template>
    <slot />
    <template v-if="$slots.actions" #footer>
      <div class="flex w-full flex-wrap items-center gap-2">
        <slot name="actions" />
      </div>
    </template>
  </VListCard>
</template>
